import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { POIModelPoiType, SettingModelSettingType } from 'api/models';
import { useGetSettingTypesByPOI } from 'api/queries/poi/poi';
import { useAutoFormBuilding } from 'app/modules/wallforming/components/auto-form/hooks/useAutoFormBuilding';
import { Catalogue } from 'app/modules/wallforming/components/catalogue-container/Catalogue';
import { CatalogueArticlePreview } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticlePreview';
import { useInputActionHandler } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import {
    applyCurrentPoiPlacementVisibility,
    isCornerPlacementPreviewActive
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { SupportingConstructionClampSettings } from 'app/modules/wallforming/components/setting-container/anchor-placement/SupportingConstructionClampSettings';
import { AutomatedPlacementAction } from 'app/modules/wallforming/components/setting-container/automated-placement/AutomatedPlacementAction';
import {
    placementSettingTypes,
    PlacementTypeHeader
} from 'app/modules/wallforming/components/setting-container/automated-placement/PlacementTypeHeader';
import { ConnectorPlacementSettings } from 'app/modules/wallforming/components/setting-container/connector-placement/ConnectorPlacementSettings';
import { CornerPlacementContainer } from 'app/modules/wallforming/components/setting-container/corner-placement/CornerPlacementContainer';
import { useGetAutomatedCornerPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useGetAutomatedCornerPlacement';
import { useHandleConnectorPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useHandleConnectorPlacement';
import { useHandlePanelPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useHandlePanelPlacement';
import { PanelPlacementSettings } from 'app/modules/wallforming/components/setting-container/panel-placement/PanelPlacementSettings';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const AutomatedPlacementContainer = () => {
    const {
        selectedPoiType,
        selectedSettingType,
        selectedSettingView,
        automatedPlacementTab,
        setAutomatedPlacementTab,
        setSelectedSettingType,
        isPlacementDataInitialized
    } = usePlacementStore(
        useShallow((state) => ({
            selectedPoiType: state.poiSelection.current.filterType,
            selectedSettingType: state.selectedSettingType,
            automatedPlacementTab: state.automatedPlacementTab,
            setAutomatedPlacementTab: state.setAutomatedPlacementTab,
            setSelectedSettingType: state.setSelectedSettingType,
            selectedSettingView: state.selectedSettingView,
            isPlacementDataInitialized: state.isPlacementDataInitialized
        }))
    );

    const [areCornerSolutionsLoading, setAreCornerSolutionsLoading] = useState<boolean>(false);
    const loadCornerSolutions = useGetAutomatedCornerPlacement({ setLoadingState: setAreCornerSolutionsLoading });
    const { handlePanelPlacementStart } = useHandlePanelPlacement();
    const { handleConnectorPlacementStart } = useHandleConnectorPlacement();
    const handleAnchorPlacementStart = useAutoFormBuilding({ anchorsOnly: true });

    useEffect(() => {
        const subscribed = usePlacementStore.subscribe(isCornerPlacementPreviewActive, () => {
            applyCurrentPoiPlacementVisibility(usePlacementStore.getState());
        });

        return () => subscribed();
    }, []);

    const { data: availableSettingTypesResponse, isLoading: isLoadingSettingTypes } = useGetSettingTypesByPOI(
        selectedPoiType === POIFilterType.Corner ? POIModelPoiType.PlanarFace : POIModelPoiType.StraightWall,
        {
            query: {
                queryKey: ['automated-placement-setting-types', selectedPoiType],
                enabled: selectedPoiType !== undefined,
                staleTime: 24 * 60 * 60 * 1000
            }
        }
    );

    const availableSettingTypes = useMemo(() => {
        if (selectedPoiType === undefined) {
            return [];
        }

        const availableSet = new Set(availableSettingTypesResponse?.data ?? []);
        return placementSettingTypes.filter((settingType) => availableSet.has(settingType));
    }, [availableSettingTypesResponse?.data, selectedPoiType]);

    const isAutomationViewActive = useMemo(
        () => selectedSettingView === SettingView.AUTOMATED_PLACEMENT,
        [selectedSettingView]
    );

    const isCornerPlacementActive = useMemo(
        () =>
            isAutomationViewActive &&
            selectedSettingType === SettingModelSettingType.Corners &&
            selectedPoiType === POIFilterType.Corner,
        [isAutomationViewActive, selectedSettingType, selectedPoiType]
    );

    const loadPlacementSolutions = useMemo(
        () => isCornerPlacementActive && isPlacementDataInitialized && automatedPlacementTab === PlacementTab.SOLUTIONS,
        [isCornerPlacementActive, automatedPlacementTab, isPlacementDataInitialized]
    );

    const loadAutomatedPlacement = useMemo(
        () =>
            isPlacementDataInitialized &&
            isAutomationViewActive &&
            (selectedSettingType === SettingModelSettingType.Panels ||
                selectedSettingType === SettingModelSettingType.Connectors ||
                selectedSettingType === SettingModelSettingType.Anchors),
        [isAutomationViewActive, isPlacementDataInitialized, selectedSettingType]
    );

    useEffect(() => {
        if (isCornerPlacementActive) {
            setAutomatedPlacementTab(PlacementTab.SOLUTIONS);
        }
    }, [setAutomatedPlacementTab, isCornerPlacementActive]);

    useEffect(() => {
        if (availableSettingTypes.length === 0) {
            return;
        }

        if (!availableSettingTypes.includes(selectedSettingType)) {
            setSelectedSettingType(availableSettingTypes[0]);
        }
    }, [selectedSettingType, availableSettingTypes, setSelectedSettingType]);

    useEffect(() => {
        if (loadPlacementSolutions) {
            void loadCornerSolutions();
        }
    }, [loadCornerSolutions, loadPlacementSolutions]);

    const handleOnAutomatedPlacementStart = useCallback(() => {
        if (!isPlacementDataInitialized) {
            return;
        }

        if (selectedSettingType === SettingModelSettingType.Panels) {
            handlePanelPlacementStart();
            return;
        }

        if (selectedSettingType === SettingModelSettingType.Connectors) {
            handleConnectorPlacementStart();
            return;
        }

        if (selectedSettingType === SettingModelSettingType.Anchors) {
            void handleAnchorPlacementStart();
        }
    }, [
        handleAnchorPlacementStart,
        handleConnectorPlacementStart,
        handlePanelPlacementStart,
        isPlacementDataInitialized,
        selectedSettingType
    ]);

    useInputActionHandler(InputActionId.PlacementExecute, () => {
        if (loadAutomatedPlacement) {
            handleOnAutomatedPlacementStart();
        }
    });

    const handleOnKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const state = usePlacementStore.getState();
            if (isInputElement(event.target)) {
                return;
            }

            const selectedSettingTypeIndex =
                Number.parseInt(event.code.match(/^(?:Digit|Numpad)([1-9])$/)?.[1] ?? '0', 10) - 1;
            if (
                selectedSettingTypeIndex >= 0 &&
                selectedSettingTypeIndex < placementSettingTypes.length &&
                availableSettingTypes.includes(placementSettingTypes[selectedSettingTypeIndex]) &&
                isAutomationViewActive
            ) {
                event.preventDefault();
                state.setSelectedSettingType(placementSettingTypes[selectedSettingTypeIndex]);
            }
        },
        [availableSettingTypes, isAutomationViewActive]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleOnKeyDown);
        return () => window.removeEventListener('keydown', handleOnKeyDown);
    }, [handleOnKeyDown]);

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <PlacementTypeHeader
                actionNode={
                    <div className="flex items-center">
                        <AutomatedPlacementAction settingType={selectedSettingType} />
                    </div>
                }
                availableSettingTypes={availableSettingTypes}
                isLoading={isLoadingSettingTypes}
                selectedSettingType={selectedSettingType}
                onSelectSettingType={setSelectedSettingType}
            />

            <div className="m-2 flex min-h-0 flex-1 flex-col gap-2">
                <div className="flex min-h-0 max-h-1/3 flex-none flex-col overflow-hidden">
                    <div className="shrink-0">
                        {selectedSettingType === SettingModelSettingType.Connectors ? (
                            <ConnectorPlacementSettings />
                        ) : selectedSettingType === SettingModelSettingType.Anchors ? (
                            <SupportingConstructionClampSettings />
                        ) : selectedSettingType === SettingModelSettingType.Panels ? (
                            <PanelPlacementSettings />
                        ) : null}
                    </div>
                </div>

                {selectedSettingType === SettingModelSettingType.Corners ? (
                    <CornerPlacementContainer areCornerSolutionsLoading={areCornerSolutionsLoading} />
                ) : (
                    <div className="grid min-h-0 h-full min-w-0 flex-1 grid-rows-[minmax(11rem,1.25fr)_minmax(7rem,0.75fr)] gap-2 overflow-hidden">
                        <div className="min-h-0 min-w-0 overflow-hidden" data-automated-catalogue>
                            <Catalogue settingType={selectedSettingType} showFilter={false} />
                        </div>
                        <div className="min-h-0 min-w-0 overflow-hidden">
                            <CatalogueArticlePreview />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
